import { hasActiveLlmStream } from './activeLlmStreamGuard';
import { isSalesforceEmbedFrame } from './salesforceEmbed';

/**
 * Shared "stale JS after a deploy" recovery logic, previously triplicated (and drifting)
 * across components/ErrorBoundary/ErrorBoundary.tsx, app/error.tsx, and app/global-error.tsx.
 *
 * Confirmed live, 2026-08-07: a ChunkLoadError caught by one of these boundaries reloaded the
 * page immediately mid-chat-send, aborting the in-flight /llm/chat/completions request before
 * the server ever received it -- no error, no reply, message just gone. Same failure class
 * BuildVersionChecker was fixed for (utils/activeLlmStreamGuard.ts), from a second, independent
 * trigger: a real render-time error rather than a periodic version-mismatch poll. This module
 * folds all three reload paths through the same guard so neither can reload out from under an
 * active send again.
 */

const RELOAD_COUNT_KEY = 'mb_error_reload_count';
const MAX_AUTO_RELOADS = 2;
const BUILD_ID_STORAGE_KEY = 'mb_build_id';
// How often to recheck while deferring, and the longest we'll defer before reloading
// unconditionally -- a render already crashed here (unlike BuildVersionChecker's proactive
// poll), so recovery can't wait indefinitely.
const DEFER_RECHECK_MS = 3_000;
const MAX_DEFER_MS = 30_000;

export function isDeploymentError(message: string): boolean {
  return (
    message.includes('Failed to find Server Action') ||
    message.includes('ChunkLoadError') ||
    message.includes('Loading chunk') ||
    message.includes('Loading CSS chunk')
  );
}

function reloadOnceStreamClear(waitedMs = 0) {
  if (!hasActiveLlmStream() || waitedMs >= MAX_DEFER_MS) {
    window.location.reload();
    return;
  }
  setTimeout(() => reloadOnceStreamClear(waitedMs + DEFER_RECHECK_MS), DEFER_RECHECK_MS);
}

/**
 * Call from a caught render error. Returns true if this was a deployment error and a reload
 * has been performed or deferred (the caller can skip its normal fallback UI); false if the
 * caller should show its normal recovery UI instead (not a deployment error, or the
 * reload-loop guard has already been hit twice this session). Never reloads a Salesforce
 * embed frame (`isSalesforceEmbedFrame`): its Canvas sign-in cannot survive a reload.
 */
export function handleDeploymentErrorReload(errorMessage: string): boolean {
  if (!isDeploymentError(errorMessage)) return false;
  if (isSalesforceEmbedFrame()) return false;

  try {
    const count = parseInt(sessionStorage.getItem(RELOAD_COUNT_KEY) || '0', 10);
    if (count >= MAX_AUTO_RELOADS) return false;
    sessionStorage.setItem(RELOAD_COUNT_KEY, String(count + 1));
  } catch {
    // sessionStorage unavailable -- fall through to a best-effort reload below anyway,
    // matching this logic's behavior before it was shared across three call sites.
  }

  localStorage.removeItem(BUILD_ID_STORAGE_KEY);
  reloadOnceStreamClear();
  return true;
}
